import clsx from "clsx";
import dayjs from "dayjs";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import usePostData from "../../api/usePostData";
import DatePickerInput from "../../common/DatePickerInput";
import SelectInput from "../../common/SelectInput";
import TimePicker from "../../common/TimePicker";
import useApiEffect from "../../utils/useApiEffect";
import { AirportVehicleDetailsValidation } from "../../utils/validations";
import VehicleTypeModal from "../VehicleTypeModal/VehicleTypeModal";
import { AirportRideContext } from "./AirportRideContext";
import styles from "./checkbox.module.css";
import { bookingTracking, vehicleSelectTracking } from "../../utils/mixpanel";
import { message } from "antd";

/**
 * The Vehicle details step of the airport ride trip
 *
 * It shows a form containing the following:
 *  - The vehicle name. The options of the cars come from the backend
 *  - An option whether the ride is intended to be shared with others or not. This option is only shown if the backend decides that there is an available discount for the given data provided by the user
 *  - The number of the seats to be shared with others. Gets shown only when the shared ride option is selected, and the number of seats is maxed by the selected vehicle.
 *  - The date the ride is intended to be in.
 *  - The time the ride is intended to be in. The combination of date and time can't be in the past logically, and also can't be before a buffer duration (call `bookingAllowedTime`) decided from the backend
 *
 * @returns {JSX.Element}
 */

const VehicleDetailsStep = () => {
  const { t } = useTranslation("global");
  const resetRef = useRef();
  const vehicleTypeData = useSelector(
    (state) => state.cachedRequests.vehiclesRequest
  );
  const {
    firstStepValues,
    secondStepValues,
    setSecondStepValues,
    setActiveTab,
    setErrorMessage, errorMessage 
  } = useContext(AirportRideContext);
  const bookingSettings = useSelector(
    (state) => state.cachedRequests.bookingSettings
  );
  const user = useSelector((state) => state.authData.user);
  const isRegistered = !!user;
  const [showSharedRide, setShowSharedRide] = useState(
    secondStepValues.discount && secondStepValues.discount > 0
  );
  const [discount, setDiscount] = useState(secondStepValues.discount);
  const { postData: checkDiscountRequest } = usePostData();
  const { postData: checkTripBookingTime } = usePostData();
  const checkDiscount = async (vehicleName, arrivalDate, arrivalTime) => {
    const zone = firstStepValues.zone;
    if (!vehicleName || !arrivalDate || !arrivalTime || !zone) {
      return;
    }
    const res = await checkDiscountRequest({
      route: "api/method/airport_transport.api.bookings.get_ride_discount",
      data: {
        vehicle_type: vehicleName,
        arrival_date: arrivalDate,
        arrival_time: arrivalTime,
        service_type: "Airport Trip",
        zone,
      },
    });
    if (res.data?.data) {
      setShowSharedRide(true);
      setDiscount(res.data.data);
    } else {
      setDiscount(0);
    }
  };
  useEffect(() => {
    if (resetRef.current) {
      resetRef.current.click();
    }
  }, [vehicleTypeData]);
  useApiEffect(() => {
    if (secondStepValues.discount) {
      return;
    }
    checkDiscount(
      vehicleTypeData?.message?.[0]?.short_name,
      dayjs().format("YYYY-MM-DD"),
      dayjs().format("HH:mm")
    );
  }, [vehicleTypeData]);

  if (!vehicleTypeData?.message) {
    return <div className="min-h-[378px]"></div>;
  }


// _________________________ C H E C K I N I T I A L  B O O K I N G ______________________________//
  const Check_Trip_Exist_Before = async (date, time) => {
    try {
      const response = await checkTripBookingTime({
        route: "api/method/airport_transport.api.bookings.check_booking",
        data: {arrival_date:date.format("YYYY-MM-DD"), arrival_time: time.format("HH:mm")},
        customError: true
      });
      if (response.status !== 200) {
        if(response?.error?.includes("You already have")){
          message.error(t("have_a_trip_for_this_date_and_time")); 
          setErrorMessage(response?.error);
        }
      }
      else{
        return true;
      }
    } catch (error) {
      message.error("Booking error occurred");
      return false;
    }
  };
// _______________________________________________________________________________________________//

  const onSubmit = async (values) => {
    if(isRegistered){
      const bookingTripInitially = await Check_Trip_Exist_Before(values.arrivalDate, values.arrivalTime);
        if (!bookingTripInitially) {
          return; // Stop execution if inializing booking fails
        }
    }
    setSecondStepValues({
      vehicleType: values.vehicleTypeName,
      sharedRide: values.sharedRide,
      seatNumber: values.seatNumber,
      arrivalDate: values.arrivalDate,
      arrivalTime: values.arrivalTime,
      vehicleTypeIndex: values.vehicleTypeIndex,
      discount,
    });
    bookingTracking({
      user: user?.email,
      service: "Airport",
      step: "Vehicle Details Step",
    });
    setActiveTab(3);
  };
  const bookingAllowedTime = bookingSettings.booking_allowed_time / 60;
  const vehicles = vehicleTypeData.message;
  return (
    <div>
      <Formik
        initialValues={{
          vehicleTypeIndex: secondStepValues.vehicleTypeIndex,
          vehicleTypeName:
            vehicles?.[secondStepValues.vehicleTypeIndex]?.short_name,
          sharedRide: secondStepValues.sharedRide || false,
          seatNumber: secondStepValues.seatNumber || undefined,
          arrivalDate:
            secondStepValues.arrivalDate ||
            dayjs().add(bookingAllowedTime, "hours"),
          arrivalTime:
            secondStepValues.arrivalTime ||
            dayjs().add(bookingAllowedTime, "hours"),
        }}
        onSubmit={onSubmit}
        validationSchema={AirportVehicleDetailsValidation(
          t,
          bookingAllowedTime
        )}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form className="flex flex-col gap-4">
            <input
              className="hidden"
              onClick={() => {
                setFieldValue(
                  "vehicleTypeName",
                  vehicles?.[values.vehicleTypeIndex]?.short_name
                );
              }}
              ref={resetRef}
            />
            <VehicleTypeModal
              VehicleTypeWithService={vehicles}
              vehicleTypeName={values.vehicleTypeName}
              setVehicleTypeName={(vehicle, idx) => {
                setFieldValue("seatNumber", undefined);
                setFieldValue("vehicleTypeName", vehicle);
                setFieldValue("vehicleTypeIndex", idx);
                checkDiscount(
                  vehicle,
                  values.arrivalDate.format("YYYY-MM-DD"),
                  values.arrivalTime.format("HH:mm")
                );
                vehicleSelectTracking({
                  user: user?.email,
                  service: "Airport",
                  vehicle,
                });
              }}
            />
            {!!showSharedRide && (
              <div className="border border-bg_light_gray p-2 mt-3">
                <p className="text-lg text-text_black font-bold">
                  {t("hero.shared_ride_text")}
                </p>
                <div className="mb-3 flex flex-row justify-between items-center">
                  <div>
                    <div className="text-sm text-text_black">
                      <span>{`${t("hero.select_shared_ride_text")}`} </span>
                      <span className="text-text_steel_blue font-bold">
                        {t("hero.discount")} {discount}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <Field
                      type={"checkbox"}
                      name={"sharedRide"}
                      onChange={(e) => {
                        const { checked } = e.target;
                        setFieldValue("seatNumber", undefined);
                        setFieldValue("sharedRide", checked);
                      }}
                      className={clsx(
                        "mr-2 text-gray-900 text-sm block transform scale-150",
                        styles.checkbox_custom
                      )}
                    />
                  </div>
                </div>
              </div>
            )}
            {values.sharedRide && (
              <SelectInput
                name={"seatNumber"}
                onChange={(val) => {
                  setFieldValue("seatNumber", val);
                }}
                label={t("hero.seat_number_text")}
                placeholder={t("hero.select_seat_number")}
                customError={true}
                options={Array.from({
                  length: vehicles.find(
                    (vehicle) => vehicle.short_name === values.vehicleTypeName
                  ).seats,
                }).map((_, idx) => ({ label: idx + 1, value: idx + 1 }))}
              />
            )}
            {/* <div className="error-message">{errors.seatNumber}</div> */}

            <div className="flex flex-col gap-1">
              <label htmlFor="arrivalDate">{t("hero.arrival_date_text")}</label>
              <DatePickerInput
                format={"YYYY-MM-DD"}
                value={values.arrivalDate}
                onChange={(date) => {
                  setFieldValue("arrivalDate", date);
                  setErrorMessage("");
                }}
                style={{borderColor:"red"}}
                name="arrivalDate"
                disabledDate={(current) => {
                  return (
                    current &&
                    current <
                      dayjs().add(bookingAllowedTime, "hours").startOf("day")
                  );
                }}
              />
              {errors.arrivalDate && touched.arrivalDate && (
                <p className="text-text_warning text-sm mt-1 text-start">
                  {t(errors.arrivalDate)}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="arrivalDate">{t("hero.arrival_time_text")}</label>
              <TimePicker
                value={values.arrivalTime}
                onChange={(time) => {
                  setFieldValue("arrivalTime", time);
                  setErrorMessage("");
                }}
                name="arrivalTime"
              />
              <ErrorMessage name="arrivalTime" render={renderErrorMessage} />
            </div>
             {errorMessage && <p className="font-[500] text-text_warning text-[15px] mt-1 text-start">{t("have_a_trip_for_this_date_and_time")}</p>}

            <div className="flex">
              <button
                type="button"
                className="bg-bg_btn_back w-1/2 text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2"
                onClick={() => {
                  setActiveTab(1);
                }}
              >
                {t("previous_text")}
              </button>
              <button
                type="submit"
                className="bg-background_steel_blue w-1/2 text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 mb-2"
              >
                {t("submit_text")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const renderErrorMessage = (msg) => (
  <span
    style={{
      color: "#ff4d4f",
      fontSize: "small",
    }}
  >
    {msg}
  </span>
);

export default VehicleDetailsStep;
